import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import CommunityPostList from './CommunityPostList';
import {
  clearViewerSession,
  getViewerSession,
  listViewerFeed,
  reactToPost,
  removeReaction,
  resolveDefaultChannel,
  updateViewerPreference,
} from '../../services/communityService';
import './Community.css';

const CommunityViewerPage = () => {
  useUiLanguage();
  const { t, i18n } = useTranslation('community');
  const { isRTL } = useLanguage();
  const [session, setSession] = useState(() => getViewerSession());
  const [selectedChannel, setSelectedChannel] = useState(() => resolveDefaultChannel(getViewerSession().allowedChannels));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reactionBusyByPost, setReactionBusyByPost] = useState({});
  const [prefsBusy, setPrefsBusy] = useState({});
  const [channelSubscriptions, setChannelSubscriptions] = useState(() => ({
    talent_pool: true,
    workforce: true,
  }));

  const allowedChannels = useMemo(
    () => session?.allowedChannels || {},
    [session]
  );

  const availableChannels = useMemo(() => {
    return Object.entries(allowedChannels)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([channel]) => channel);
  }, [allowedChannels]);

  const getChannelLabel = (channel) => {
    if (channel === 'talent_pool') return t('viewer.channels.talentPool');
    if (channel === 'workforce') return t('viewer.channels.workforce');
    return channel.replace(/[_-]+/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
  };

  useEffect(() => {
    if (!availableChannels.includes(selectedChannel)) {
      setSelectedChannel(resolveDefaultChannel(allowedChannels));
    }
  }, [availableChannels, allowedChannels, selectedChannel]);

  const loadFeed = async ({ append = false, cursor = undefined } = {}) => {
    if (!session?.viewerToken || !selectedChannel) return;

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError('');

      const payload = await listViewerFeed({
        viewerToken: session.viewerToken,
        channel: selectedChannel,
        limit: 20,
        cursor,
      });

      setPosts((prev) => (append ? [...prev, ...(payload.posts || [])] : payload.posts || []));
      setNextCursor(payload.next_cursor || null);
    } catch (err) {
      console.error('Failed to load viewer feed:', err);
      const message = err.message || t('viewer.loadFailed', 'Failed to load feed.');
      if (message.toLowerCase().includes('session')) {
        clearViewerSession();
        setSession(getViewerSession());
      }
      setError(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setChannelSubscriptions({
      talent_pool: Boolean(allowedChannels.talent_pool),
      workforce: Boolean(allowedChannels.workforce),
    });
  }, [allowedChannels]);

  useEffect(() => {
    if (!session?.viewerToken) return;
    loadFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.viewerToken, selectedChannel]);

  const handleReact = async (post, reactionType) => {
    if (!session?.viewerToken) return;

    const postId = post.id;
    const currentReaction = post.myReaction;

    setReactionBusyByPost((prev) => ({ ...prev, [postId]: true }));
    setError('');

    try {
      if (currentReaction === reactionType) {
        await removeReaction({ viewerToken: session.viewerToken, postId });
      } else {
        await reactToPost({
          viewerToken: session.viewerToken,
          postId,
          reactionType,
        });
      }

      await loadFeed();
    } catch (err) {
      console.error('Failed to save reaction:', err);
      setError(err.message || t('viewer.reactions.saveFailed', 'Could not save reaction.'));
    } finally {
      setReactionBusyByPost((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleToggleSubscription = async (channel) => {
    if (!session?.viewerToken) return;

    const nextSubscribed = !channelSubscriptions[channel];
    setPrefsBusy((prev) => ({ ...prev, [channel]: true }));
    setError('');

    try {
      await updateViewerPreference({
        viewerToken: session.viewerToken,
        channel,
        subscribed: nextSubscribed,
      });
      setChannelSubscriptions((prev) => ({ ...prev, [channel]: nextSubscribed }));
      setSuccess(t('viewer.preferencesSaved'));
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      console.error('Failed to update preference:', err);
      setError(err.message || t('viewer.preferencesFailed', 'Could not update preference.'));
    } finally {
      setPrefsBusy((prev) => ({ ...prev, [channel]: false }));
    }
  };

  if (!session?.viewerToken) {
    return (
      <div className="community-public-wrap" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="community-public-card">
          <h2 className="community-page-title" style={{ fontSize: 24 }}>{t('access.title')}</h2>
          <p className="community-message error" style={{ marginTop: 14 }}>{t('viewer.missingSession', 'Your feed session is missing or expired.')}</p>
          <p className="community-muted" style={{ marginTop: 10 }}>
            <Link to="/login" style={{ color: '#9ec0ff' }}>{t('viewer.goLogin', 'Go to login')}</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="community-public-wrap" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="community-public-card">
        <h2 className="community-page-title" style={{ fontSize: 24 }}>{t('viewer.title')}</h2>

        <div className="community-channel-switches">
          {availableChannels.map((channel) => (
            <button
              key={channel}
              className={`community-switch ${selectedChannel === channel ? 'active' : ''}`}
              onClick={() => setSelectedChannel(channel)}
              type="button"
            >
              {getChannelLabel(channel)}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 12 }}>
          {availableChannels.map((channel) => {
            const isSubscribed = channelSubscriptions[channel];
            const busy = prefsBusy[channel];

            return (
              <button
                key={channel}
                type="button"
                className={`community-switch ${isSubscribed ? 'active' : ''}`}
                disabled={busy}
                onClick={() => handleToggleSubscription(channel)}
                style={{ marginInlineEnd: 8, marginBottom: 8 }}
              >
                {getChannelLabel(channel)}
                {' · '}
                {busy ? '...' : isSubscribed ? t('viewer.unsubscribe') : t('viewer.resubscribe')}
              </button>
            );
          })}
        </div>

        {error && <div className="community-message error">{uiText(error)}</div>}
        {success && <div className="community-message success">{success}</div>}

        <div style={{ marginTop: 12 }}>
          {loading ? (
            <p className="community-skeleton">{t('viewer.loading')}</p>
          ) : (
            <CommunityPostList
              posts={posts}
              locale={i18n.language}
              emptyText={t('viewer.empty')}
              showReactions
              onReact={handleReact}
              reactionBusyByPost={reactionBusyByPost}
              reactionLabels={{
                totalPrefix: t('viewer.reactions.total', 'Total reactions'),
                like: t('viewer.reactions.like'),
                celebrate: t('viewer.reactions.celebrate'),
                support: t('viewer.reactions.support'),
              }}
              channelLabels={availableChannels.reduce((acc, channel) => {
                acc[channel] = getChannelLabel(channel);
                return acc;
              }, {})}
            />
          )}
        </div>

        {nextCursor && (
          <div style={{ marginTop: 12 }}>
            <button
              className="community-btn secondary"
              onClick={() => loadFeed({ append: true, cursor: nextCursor })}
              disabled={loadingMore}
            >
              {loadingMore ? t('viewer.loadingMore', 'Loading...') : t('viewer.loadMore', 'Load more')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityViewerPage;
